import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import api, { unitAPI } from '../../services/api';
import { formatDwellingUnit } from '../../utils/brandingUtils';
import PageTitle from '../shared/PageTitle';
import { toast } from 'react-toastify';

const MyVillas = () => {
  const { currentUser } = useContext(AuthContext);
  const { formatCurrency, settings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState(null);
  const [ownerInfo, setOwnerInfo] = useState(null);
  
  // For unit naming
  const unitSingular = formatDwellingUnit(settings);
  const unitPlural = formatDwellingUnit(settings, null, true);
  
  // Custom styles based on theme settings
  const blockTitleStyle = {
    backgroundColor: settings.block_title_bg_color || '#f8f9fa',
    color: settings.block_title_color || '#8B4513'
  };

  // Fetch payments data and villa info
  useEffect(() => {
    const fetchPaymentsAndVilla = async () => {
      setLoading(true);
      try {
        // Import financialAPI
        const { financialAPI } = require('../../services/api');
        // Use the consistent getOwnerPayments function
        try {
          const paymentsResponse = await financialAPI.getOwnerPayments();
          if (paymentsResponse.data && Array.isArray(paymentsResponse.data.payments)) {
            setPayments(paymentsResponse.data);
            setError(null);
          } else {
            setPayments([]);
          }
        } catch (paymentError) {
          console.error('Error fetching payment history:', paymentError);
        }
        
      } catch (err) {
        console.error('Error in fetchPaymentsAndVilla:', err);
        setPayments([]);
        setError('Failed to load payment data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPaymentsAndVilla();
  }, [currentUser]);

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        setLoading(true);
        console.log('Fetching units for owner...');
        
        // Use the consistent API function from services/api.js
        const response = await unitAPI.getOwnerUnits();
        console.log('Units response:', response.data);
        
        if (Array.isArray(response.data)) {
          // Get current date for mock payment dates
          const today = new Date();
          const currentMonth = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0');
          const currentMonthDay = `${currentMonth}-${String(Math.min(today.getDate(), 15)).padStart(2, '0')}`;
          
          // Add mock data for demo purposes if real data is missing fields
          const enhancedUnits = response.data.map(unit => ({
            ...unit,
            size: unit.size || 'N/A',
            bedrooms: unit.bedrooms || 'N/A',
            bathrooms: unit.bathrooms || 'N/A',
            built_year: unit.built_year || 'N/A',
            contact_phone: unit.contact_phone || 'N/A',
            maintenance_fee: unit.maintenance_fee || 'N/A',
            status: unit.status || 'Occupied',
            last_payment_date: unit.last_payment_date || currentMonthDay // Use current month's date instead of hardcoded
          }));
          
          // Store the original API response in session storage for comparison
          sessionStorage.setItem('my_units_data', JSON.stringify(response.data));
          console.log(`MyVillas units: ${response.data.length} - First unit: ${response.data[0]?.villa_number || 'none'}`);

          setUnits(enhancedUnits);
          if (enhancedUnits.length > 0 && enhancedUnits[0].owner_id) {
            try {
              const ownerRes = await api.get(`/api/users/${enhancedUnits[0].owner_id}`);
              console.log('ownerRes', ownerRes)
              setOwnerInfo(ownerRes.data);
            } catch (ownerErr) {
              console.error('Error fetching owner info:', ownerErr);
            }
          } else {
            setOwnerInfo(null);
          }
        } else {
          setUnits([]);
          // Store empty array in session storage
          sessionStorage.setItem('my_units_data', JSON.stringify([]));
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching units:', err);
        setError('Failed to load units. ' + (err.message || ''));
        
        // Provide mock data if API fails completely
        provideMockUnits();
      } finally {
        setLoading(false);
      }
    };
    
    // Create mock units data with current dates when API fails
    const provideMockUnits = () => {
      console.log('Using mock units data due to API failure');
      
      // Generate current date for last payment
      const today = new Date();
      const currentMonth = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0');
      const currentMonthDay = `${currentMonth}-15`; // 15th of current month
      
      // Mock unit data
      const mockUnits = [
        {
          id: 1,
          villa_number: 'V-22',
          unit_type: 'Villa',
          owner_id: currentUser?.id,
          size: '1500 sq ft',
          bedrooms: 3,
          bathrooms: 2,
          built_year: '2020',
          maintenance_fee: 1000,
          status: 'Occupied',
          last_payment_date: currentMonthDay
        }
      ];
      
      setUnits(mockUnits);
    };

    if (currentUser && currentUser.role === 'owner') {
      fetchUnits();
    }
  }, [currentUser, settings]);

  const handleVisitorApprovalChange = async (unitId, checked) => {
    try {
      // Optimistically update UI
      setUnits(prevUnits =>
        prevUnits.map(unit =>
          unit.id === unitId
            ? { ...unit, visitor_approval_required: checked ? 1 : 0 }
            : unit
        )
      );

      // Send API request to update the backend
      const result = await unitAPI.updateUnitVisitorApproval(unitId, { visitor_approval_required: checked ? 1 : 0 });
      if(result) {
        toast.success(result.data?.message);
      }
    } catch (err) {
      toast.error('Failed to update visitor approval setting.');
      // Optionally, revert UI change if API fails
      setUnits(prevUnits =>
        prevUnits.map(unit =>
          unit.id === unitId
            ? { ...unit, visitor_approval_required: checked ? 0 : 1 }
            : unit
        )
      );
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <PageTitle 
        icon="fa-home" 
        title={`My ${unitPlural}`} 
        action={
          <Link to="/owner/dashboard" className="btn btn-outline-primary">
            <i className="fas fa-arrow-left me-2"></i> Back to Dashboard
          </Link>
        }
      />
      <p className="text-muted mb-4">View and manage your {unitSingular.toLowerCase()} properties</p>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {units.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          You don&apos;t have any {unitPlural.toLowerCase()} assigned to your account. Please contact the administration if this is an error.
        </div>
      ) : (
        <div className="row">
          {units.map(unit => (
            <div key={unit.id} className="col-md-12 col-lg-6 mb-4">
              <div className="card shadow h-100">
                <div className="card-header" style={blockTitleStyle}>
                  <h5 className="mb-0">{unit.unit_type || unitSingular} {unit.villa_number}</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span><i className="fas fa-expand-arrows-alt me-2"></i> Size:</span>
                      <span className="fw-bold">{unit.size}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span><i className="fas fa-bed me-2"></i> Bedrooms:</span>
                      <span className="fw-bold">{unit.bedrooms}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span><i className="fas fa-bath me-2"></i> Bathrooms:</span>
                      <span className="fw-bold">{unit.bathrooms}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span><i className="fas fa-calendar-alt me-2"></i> Built:</span>
                      <span className="fw-bold">{unit.built_year}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span><i className="fas fa-phone me-2"></i> Contact:</span>
                      <span className="fw-bold">{ownerInfo?.phone}</span>
                    </div>
                  </div>
                  
                  <div className="alert alert-info d-block mb-0">
                    <div className="d-flex justify-content-between">
                      <strong>Monthly Maintenance:</strong>
                      <span>{formatCurrency(unit.maintenance_fee)}</span>
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                      <small>Last Payment:</small>
                      <small>
                        { payments?.last_payment_date ? payments?.last_payment_date : 'No payments' }
                      </small>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-center mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      name={`visitor_approval_required_${unit.id}`}
                      id={`visitor_approval_required_${unit.id}`}
                      checked={!!unit.visitor_approval_required}
                      onChange={e => handleVisitorApprovalChange(unit.id, e.target.checked)}
                    />
                    <label className="form-check-label ms-2" htmlFor={`visitor_approval_required_${unit.id}`}>
                      Visitor Approval Required
                    </label>
                  </div>
                </div>
                
                <div className="card-footer bg-light">
                  <div className="d-grid">
                    <Link to={`/owner/unit/${unit.id}`} className="btn btn-primary">
                      <i className="fas fa-info-circle me-2"></i> Unit Details
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyVillas; 